import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetWorkflowQuery,
  useUpdateWorkflowStatusMutation,
  useGetWorkflowExecutionsQuery,
  useCancelWorkflowExecutionMutation,
  useGetTagsQuery,
  useGetDealStagesQuery,
  useGetMembersQuery,
} from "@/store/rest_api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Pencil, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { TRIGGER_LABELS, ACTION_LABELS, formatDelay } from "@/components/pages/workflows/WorkflowConstants";
import ExecutionList from "@/components/pages/workflows/ExecutionList";
import WorkflowFormDialog from "@/components/pages/workflows/WorkflowFormDialog";
import type { WorkflowActionType } from "@/model/interface/Workflow";
import { memberName } from "@/util/member";

export default function WorkflowDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [executionsPage, setExecutionsPage] = useState(0);

  const { data: workflow, isLoading } = useGetWorkflowQuery(id!);
  const { data: executionsData } = useGetWorkflowExecutionsQuery({ workflowId: id!, page: executionsPage, size: 10 });
  const [updateStatus] = useUpdateWorkflowStatusMutation();
  const [cancelExecution] = useCancelWorkflowExecutionMutation();
  const { data: tags = [] } = useGetTagsQuery({});
  const { data: stages = [] } = useGetDealStagesQuery({});
  const { data: members = [] } = useGetMembersQuery({ status: "ACTIVE" });

  const handleToggleStatus = async () => {
    if (!workflow) return;
    const newStatus = workflow.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await updateStatus({ id: workflow.id, status: newStatus }).unwrap();
      toast.success(newStatus === "ACTIVE" ? "Workflow activated" : "Workflow deactivated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleCancel = async (executionId: string) => {
    try {
      await cancelExecution(executionId).unwrap();
      toast.success("Execution cancelled");
    } catch {
      toast.error("Failed to cancel execution");
    }
  };

  const getActionConfigSummary = (actionType: WorkflowActionType, configStr?: string): string => {
    if (!configStr) return "";
    try {
      const config = JSON.parse(configStr);
      switch (actionType) {
        case "SEND_SMS":
          return config.message ? `"${config.message.substring(0, 50)}${config.message.length > 50 ? "..." : ""}"` : "";
        case "SEND_EMAIL":
          return config.subject || "";
        case "CREATE_TASK":
          return config.title || "";
        case "ADD_TAG":
        case "REMOVE_TAG": {
          const tag = tags.find((tag) => tag.id === config.tagId);
          return tag?.name || "";
        }
        case "MOVE_DEAL_STAGE": {
          const stage = stages.find((stage) => stage.id === config.stageId);
          return stage?.name || "";
        }
        case "ASSIGN_TO_MEMBER": {
          const member = members.find((member) => String(member.id) === String(config.membershipId));
          return member ? memberName(member, "") : "";
        }
        case "SEND_WEBHOOK":
          return config.url || "";
        case "SEND_NOTIFICATION":
        case "SEND_PUSH":
          return config.title || "";
        default:
          return "";
      }
    } catch {
      return "";
    }
  };

  const getTriggerConfigSummary = (): string => {
    if (!workflow?.triggerConfig) return "";
    try {
      const config = JSON.parse(workflow.triggerConfig);
      if (workflow.triggerType === "TAG_ADDED" && config.tagId) {
        const tag = tags.find((tag) => tag.id === config.tagId);
        return tag ? `Tag: ${tag.name}` : "";
      }
      if (workflow.triggerType === "DEAL_STAGE_CHANGE" && config.stageId) {
        const stage = stages.find((stage) => stage.id === config.stageId);
        return stage ? `Stage: ${stage.name}` : "";
      }
      if (workflow.triggerType === "CALL_DISPOSITION" && config.requiresFollowup === true) {
        return "Dispositions that require follow-up";
      }
      return "";
    } catch {
      return "";
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Workflow not found</p>
      </div>
    );
  }

  const triggerConfigSummary = getTriggerConfigSummary();

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/workflows")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">{workflow.name}</h1>
          <Badge variant={workflow.status === "ACTIVE" ? "default" : "secondary"}>
            {workflow.status}
          </Badge>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{workflow.status === "ACTIVE" ? "Active" : "Inactive"}</span>
            <Switch checked={workflow.status === "ACTIVE"} onCheckedChange={handleToggleStatus} />
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowEditDialog(true)}>
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </Button>
        </div>
      </div>

      {workflow.description && (
        <p className="text-sm text-muted-foreground">{workflow.description}</p>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Trigger</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{TRIGGER_LABELS[workflow.triggerType]}</span>
            {triggerConfigSummary && (
              <Badge variant="outline" className="text-xs">{triggerConfigSummary}</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Actions ({workflow.actions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {workflow.actions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No actions configured</p>
          ) : (
            <div className="space-y-3">
              {workflow.actions.map((action, index) => {
                const configSummary = getActionConfigSummary(action.actionType, action.config);
                return (
                  <div key={action.id} className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </div>
                      {index < workflow.actions.length - 1 && (
                        <div className="w-px h-6 bg-border mt-1" />
                      )}
                    </div>
                    <div className="flex-1 pb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{ACTION_LABELS[action.actionType]}</span>
                        {action.delayMinutes > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatDelay(action.delayMinutes)}
                          </Badge>
                        )}
                      </div>
                      {configSummary && (
                        <p className="text-xs text-muted-foreground mt-0.5">{configSummary}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Executions</CardTitle>
        </CardHeader>
        <CardContent>
          <ExecutionList
            data={executionsData}
            page={executionsPage}
            onPageChange={setExecutionsPage}
            onCancel={handleCancel}
          />
        </CardContent>
      </Card>

      <WorkflowFormDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        workflow={workflow}
      />
    </div>
  );
}
